// /admin/today: numbers for today, this week or the last 30 days (each compared with the
// period before), what needs attention, and the newest people.
import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { getOverview } from '../../../api/adminV2'
import { useCounts } from '../AdminLayout'
import {
  formatDate,
  formatNumber,
  LoadError,
  Loading,
  moneyFromCents,
  Pill,
  Spark,
  useLoad,
} from '../ui'

const greeting = () => {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 18) return 'Good afternoon'
  return 'Good evening'
}

const PERIODS = [
  ['today', 'Today'],
  ['week', 'This week'],
  ['30d', 'Last 30 days'],
]

// How each period is described, and what it is compared with (up to the same point)
const PERIOD_TEXT = {
  today: { intro: 'so far today', prev: 'yesterday by this time', spark: 'Last 14 days' },
  week: { intro: 'so far this week', prev: 'last week by this point', spark: 'Last 12 weeks' },
  '30d': { intro: 'in the last 30 days', prev: 'the 30 days before', spark: 'Last 12 weeks' },
}

const PERIOD_KEY = 'morsel_admin_period'
const savedPeriod = () => {
  try {
    const p = window.localStorage.getItem(PERIOD_KEY)
    return PERIODS.some(([key]) => key === p) ? p : 'today'
  } catch (e) {
    return 'today'
  }
}

// "+50%" (or "+3" when there was nothing before), and the number it is compared with
const Change = ({ now, prev, text }) => {
  const a = Number(now) || 0
  const b = Number(prev) || 0
  const diff = a - b
  let label
  if (!diff) label = 'Same'
  else if (!b) label = `+${formatNumber(diff)}`
  else label = `${diff > 0 ? '+' : ''}${Math.round((diff / b) * 100)}%`
  return (
    <span className="a-delta">
      <b className={diff < 0 ? 'a-down' : diff > 0 ? '' : 'a-flat'}>{label}</b> vs {formatNumber(b)}{' '}
      {text}
    </span>
  )
}

const targetLink = (target = {}) => {
  if (target.type === 'order')
    return `/admin/cookbook-orders${target.id ? `?order=${target.id}` : ''}`
  if (target.type === 'order_request')
    return `/admin/cookbook-orders${target.id ? `?request=${target.id}` : ''}`
  if (target.type === 'recipe') return `/admin/recipes/${target.id}`
  if (target.type === 'person') return `/admin/people/${target.id}`
  if (target.type === 'second_accounts') return '/admin/second-accounts'
  if (target.type === 'people')
    return `/admin/people${target.filter ? `?filter=${target.filter}` : ''}`
  return null
}

const ACTION_LABEL = {
  cookbook_order: 'Look',
  cookbook_request: 'See request',
  flagged_recipe: 'Review',
  ending_soon: 'See who',
  second_accounts: 'Review',
}

const thingsNeedYou = (n) => {
  if (!n) return 'Nothing needs you right now.'
  const words = ['', 'One thing needs', 'Two things need', 'Three things need', 'Four things need']
  return `${words[n] || `${n} things need`} you.`
}

const Today = () => {
  const navigate = useNavigate()
  const { setCounts } = useCounts()
  const [params, setParams] = useSearchParams()
  const period = PERIODS.some(([key]) => key === params.get('period'))
    ? params.get('period')
    : savedPeriod()
  const { data, error, loading, reload } = useLoad(() => getOverview(period), [period])

  const choosePeriod = (key) => {
    try {
      window.localStorage.setItem(PERIOD_KEY, key)
    } catch (e) {
      // Private windows may refuse; the address bar still has it
    }
    setParams({ period: key }, { replace: true })
  }

  useEffect(() => {
    if (data && setCounts) {
      const items = data.attention || []
      setCounts({
        flagged: items.filter((i) => i.kind === 'flagged_recipe').length,
        orders: items.filter((i) => ['cookbook_order', 'cookbook_request'].includes(i.kind)).length,
        second: items.find((i) => i.kind === 'second_accounts')?.count || 0,
      })
    }
  }, [data, setCounts])

  if (error && !data) return <LoadError error={error} onRetry={reload} />
  if (loading && !data) return <Loading />

  const s = data?.stats || {}
  const words = PERIOD_TEXT[s.period] || PERIOD_TEXT[period]
  const attention = data?.attention || []
  const newest = data?.newest_people || []

  return (
    <>
      <div className="a-head">
        <div>
          <h1>{greeting()}</h1>
          <p className="a-sub">
            Here's Morsel {words.intro}. {thingsNeedYou(attention.length)}
          </p>
        </div>
        <div className="a-seg" role="group" aria-label="Show numbers for">
          {PERIODS.map(([key, label]) => (
            <button
              type="button"
              key={key}
              aria-pressed={period === key}
              onClick={() => choosePeriod(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <section
        className={`a-stats${loading ? ' a-busy' : ''}`}
        aria-label={PERIODS.find(([key]) => key === period)[1]}
      >
        <div className="a-stat">
          <span className="a-label">New sign-ups</span>
          <span className="a-num">{formatNumber(s.signups?.now)}</span>
          <Change now={s.signups?.now} prev={s.signups?.prev} text={words.prev} />
          <Spark points={s.signups?.series} label={words.spark} />
        </div>
        <div className="a-stat">
          <span className="a-label">Recipes added</span>
          <span className="a-num">{formatNumber(s.recipes?.now)}</span>
          <Change now={s.recipes?.now} prev={s.recipes?.prev} text={words.prev} />
          <span className="a-delta">
            {formatNumber(s.recipes?.scanned)} scanned · {formatNumber(s.recipes?.from_link)} from
            links
          </span>
          <Spark points={s.recipes?.series} label={words.spark} />
        </div>
        <div className="a-stat">
          <span className="a-label">Cookbooks sold</span>
          <span className="a-num">{formatNumber(s.cookbooks?.now)}</span>
          <Change now={s.cookbooks?.now} prev={s.cookbooks?.prev} text={words.prev} />
          <span className="a-delta">
            {moneyFromCents(s.cookbooks?.paid_cents)} paid ·{' '}
            {moneyFromCents(s.cookbooks?.cost_cents)} Lulu cost
          </span>
          <Spark points={s.cookbooks?.series} label={words.spark} />
        </div>
        <div className="a-stat">
          <span className="a-label">Paying members · right now</span>
          <span className="a-num">{formatNumber(s.paying?.total)}</span>
          <span className="a-delta">
            {formatNumber(s.paying?.pro)} Pro · {formatNumber(s.paying?.family)} Family Pro
            {s.paying?.free_pro ? ` · ${formatNumber(s.paying.free_pro)} free Pro not counted` : ''}
          </span>
        </div>
        <div className="a-stat">
          <span className="a-label">Free accounts · right now</span>
          <span className="a-num">{formatNumber(s.free?.total)}</span>
          <span className="a-delta">{formatNumber(s.free?.at_limit)} at the 10-recipe limit</span>
        </div>
      </section>

      <div className="a-grid-side">
        <section className="a-panel">
          <div className="a-panel-head">
            <h2>Needs your attention</h2>
          </div>
          {attention.length ? (
            <div className="a-todo">
              {attention.map((item, i) => {
                const to = targetLink(item.target)
                return (
                  <div className="a-todo-item" key={`${item.kind}-${item.target?.id || i}`}>
                    <span className={`a-sev ${item.severity === 'high' ? 'a-red' : ''}`} />
                    <div>
                      <b>{item.title}</b>
                      {item.detail ? <p>{item.detail}</p> : null}
                    </div>
                    {to ? (
                      <button type="button" className="a-btn a-small" onClick={() => navigate(to)}>
                        {ACTION_LABEL[item.kind] || 'Open'}
                      </button>
                    ) : (
                      <span />
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="a-empty">All clear. No stuck orders, flagged recipes or endings soon.</p>
          )}
        </section>
        <section className="a-panel">
          <div className="a-panel-head">
            <h2>Newest people</h2>
            <button
              type="button"
              className="a-btn a-small"
              onClick={() => navigate('/admin/people')}
            >
              All people
            </button>
          </div>
          {newest.length ? (
            <div className="a-table-wrap">
              <table className="a-table">
                <tbody>
                  {newest.map((p) => (
                    <tr
                      className="a-click"
                      key={p.id}
                      onClick={() => navigate(`/admin/people/${p.id}`)}
                    >
                      <td className="a-who">
                        <b>{p.fullname || p.email}</b>
                        <span>Joined {formatDate(p.created_at)}</span>
                      </td>
                      <td className="a-right">
                        {p.maybe_second_account ? <Pill kind="warn">Second account?</Pill> : null}{' '}
                        <Pill status={p.status_key} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="a-empty">Nobody has signed up yet.</p>
          )}
        </section>
      </div>
    </>
  )
}

export default Today
